<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div class="k-asset-review" :class="`${total_score_color} lighten-5 k-asset-review-full-${full_review_showing}`">
		<div>
			<div class="k-asset-review-header d-flex align-center">
				<k-icon :color="`${total_score_color} darken-3`" class="mr-1" style="margin-top:-3px;">fas fa-star-sharp</k-icon>

				<div><b>{{site_config.asset_manager.text.review_header}}</b></div>

				<k-spacer/>

				<div class="ml-3">Total score:</div>

				<div class="mx-2" style="width:100px">
					<k-progress-linear
						v-model="total_score_pct"
						height="20"
						:color="`${total_score_color} darken-2`"
						rounded
					/>
				</div>
				
				<div v-if="lreview" :class="`${total_score_color}--text text--darken-4`"><b>{{total_score_display}} / {{lreview.max_total_score}}</b></div>

				<k-spacer/>

				<k-btn class="k-tight-btn" :color="`${total_score_color} darken-3`" small dark @click="full_review_showing = !full_review_showing">
					{{full_review_showing ? 'Hide' : 'Show'}} Full Review <k-icon small class="ml-2">fas fa-{{full_review_showing ? 'up-to' : 'down-from'}}-line</k-icon>
				</k-btn>
			</div>

			<k-expand-transition>
				<div v-show="full_review_showing">
					<div v-if="!lreview" class="d-flex justify-center align-center my-2">
						<b>{{site_config.asset_manager.text.review_rubric}}:</b>
						<div style="width:240px" class="ml-2">
							<k-select
								v-model="selected_template"
								:items="templates"
								:background-color="'#fff'"
								placeholder="Select Review Template"
								append-icon="fas fa-caret-down"
								dense outlined hide-details return-object
							/>
						</div>
					</div>

					<div v-else>
						<div
							v-for="(section, index) in sections"
							:key="'section_' + index"
							class="k-asset-review-section"
						>
							<div class="k-asset-review-section-title d-flex align-center">
								<div
									v-if="section.scale"
									class="k-asset-review-section-score mr-2" 
									:class="scale_score_class(section, lreview.sections[section.id].score)"
								>
									{{scale_value_to_show(section)}}
								</div>

								<div v-else>
									<k-icon
										class="k-asset-review-section-binary mr-2"
										:class="binary_score_class(lreview.sections[section.id].score, section)"
									>
										{{ binary_score_icon(section) }}
									</k-icon>
								</div>

								<div class="d-flex">
									<b v-html="section.title"></b>

									<k-tooltip v-if="section.required" bottom>
										<template v-slot:activator="{ on }">
											<k-icon v-on="on" class="ml-2" ref="button" x-small>as fa-asterisk</k-icon>
										</template>
										<span>Required</span>
									</k-tooltip>
								</div>
							</div>
							
							<div v-if="section.description" class="ma-2" v-html="section.description"></div>

							<div v-if="reviewer_or_publisher" :class="{'k-review-read-only': !user_can_edit}">
								<div v-if="section.scale">
									<div
										v-for="(scale_description, scale_value, scale_index) in section.scale"
										:key="scale_description + '_' + scale_index"
										class="k-asset-review-section-scale"
									>
										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												:class="scale_score_class(section, scale_value)"
												@click="scale_value_clicked(section, scale_value)"
											>
												{{ scale_icon(section, scale_value) }}
											</k-icon>

											<b
												class="ml-2 k-asset-review-section-scale-header-value"
												:class="scale_score_class(section, scale_value)"
											>
												{{scale_value}}: 
											</b>
											<div class="ml-1" v-html="template_string(scale_description)"></div>
										</div>
									</div>
								</div>

								<div v-else-if="section.required">
									<div class="k-asset-review-section-scale">
										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												:class="binary_score_class(1)"
												@click="scale_value_clicked(section, 1)"
											>
												{{ scale_icon(section, 1) }}
											</k-icon>

											<b
												class="ml-2 k-asset-review-section-scale-header-value"
												:class="binary_score_class(1)"
											>
												Approved
											</b>
										</div>
									</div>
									
									<div class="k-asset-review-section-scale">
										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												:class="binary_score_class(0)"
												@click="scale_value_clicked(section, 0)"
											>
												{{ scale_icon(section, 0) }}
											</k-icon>

											<b
												class="ml-2 k-asset-review-section-scale-header-value"
												:class="binary_score_class(0)"
											>
												Needs Improvement
											</b>
										</div>
									</div>
								</div>

								<div v-else>
									<div class="k-asset-review-section-scale">
										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												class="k-asset-review-section-score-hi"
												@click="scale_value_clicked(section, 2)"
											>
												{{ scale_icon(section, 2) }}
											</k-icon>

											<b class="k-asset-review-section-scale-header-value ml-2 k-asset-review-section-score-hi">
												Approved
											</b>
										</div>

										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												class="k-asset-review-section-score-0"
												@click="scale_value_clicked(section, 1)"
											>
												{{ scale_icon(section, 1) }}
											</k-icon>

											<b class="k-asset-review-section-scale-header-value ml-2 k-asset-review-section-score-0">
												Needs Improvement
											</b>
										</div>

										<div class="k-asset-review-section-scale-header d-flex my-2 ml-4">
											<k-icon
												style="margin-top: -3px;"
												class="k-asset-review-section-score-hi-1"
												@click="scale_value_clicked(section, 0)"
											>
												{{ scale_icon(section, 0) }}
											</k-icon>

											<b class="k-asset-review-section-scale-header-value ml-2 k-asset-review-section-score-hi-1">
												Not Applicable
											</b>
										</div>
									</div>
								</div>
							</div>

							<div class="d-flex justify-center">
								<div v-if="section.criteria">
									<k-btn v-if="!criteria_showing[section.id]" x-small color="#444" dark @click="criteria_showing[section.id] = true">Show Criteria<k-icon small class="ml-2">fas fa-list-check</k-icon></k-btn>
									<k-btn v-if="criteria_showing[section.id]" x-small color="#444" dark @click="criteria_showing[section.id] = false">Hide Criteria<k-icon small class="ml-2">fas fa-list-check</k-icon></k-btn>
								</div>
								<div class="mx-2" v-if="show_review_comments">
									<k-btn v-if="!comments_showing[section.id]" x-small color="#444" dark @click="comments_showing[section.id] = true">Show Comments<k-icon small class="ml-2">fas fa-comment</k-icon></k-btn>
									<k-btn v-if="comments_showing[section.id]" x-small color="#444" dark @click="comments_showing[section.id] = false">Hide Comments<k-icon small class="ml-2">fas fa-comment</k-icon></k-btn>
								</div>
							</div>

							<div v-if="section.criteria">
								<k-expand-transition>
									<div v-show="criteria_showing[section.id]" class="k-asset-review-section-extra ml-11">
										<div v-for="(c) in section.criteria" :key="'section_' + section.id + '_' + c.id "  class="d-flex mt-1">
											<k-checkbox
												v-model="lreview.sections[section.id].criteria[c.id]"
												:disabled="!user_can_edit"
												class="shrink mt-0 pt-0 d-inline-block"
												off-icon="far fa-square"
												on-icon="fas fa-check-square"
												color="#444"
												hide-details
												@change="criteria_changed(section, c, $event)"
											/>
											<div v-html="template_string(c.text)" style="margin-top:2px"></div>
										</div>
									</div>
								</k-expand-transition>
							</div>

							<div v-if="show_review_comments">
								<k-expand-transition>
									<div v-show="comments_showing[section.id]" class="k-asset-review-section-extra ml-11">
										<div v-if="lreview.sections[section.id].comments.length">
											<AssetReviewComment
												v-for="(c, index) in lreview.sections[section.id].comments.filter(x => x.parent_comment_id == '')"
												:key="section.id + '_' + index"
												class="mb-2"
												:comment="c" 
												:template="selected_template" 
												:review_section="lreview.sections[section.id]"
												@save_comment="save_comment(section, $event)"
												@delete_comment="delete_comment(section, $event)"
											/>
										</div>
										
										<div v-else-if="!lreview.sections[section.id].comments.length && !user_can_comment">
											<b>No Comments</b>
										</div>

										<div v-if="!new_comment[section.id] && user_can_comment">
											<k-btn x-small color="#444" dark @click="generate_new_comment(section.id)">
												New Comment<k-icon small class="ml-2">fas fa-comment</k-icon>
											</k-btn>
										</div>

										<AssetReviewCommentEditor
											v-if="new_comment[section.id]"
											:comment="new_comment[section.id]"
											@save_comment="save_comment(section, $event)"
											@cancel_comment="new_comment[section.id] = false"
										/>
									</div>
								</k-expand-transition>
							</div>
						</div>

						<div class="k-asset-review-section d-flex">
							<div class="mt-1"><b>Template: </b>{{ lreview.template.text }}</div>

							<k-spacer/>

							<k-btn v-if="$parent.user_is_admin" class="k-tight-btn" color="tertiary" small @click="reset_review">Reset Review</k-btn>
							<div v-else>To reset this review, contact system administrator.</div>
						</div>
					</div>
				</div>
			</k-expand-transition>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetReviewComment from './AssetReviewComment.vue'
import AssetReviewCommentEditor from './AssetReviewCommentEditor.vue'

export default {
	components: {AssetReviewComment, AssetReviewCommentEditor},

	props: {
		asset_review_data: { required: true },
	},

	data() { 
		return {
			full_review_showing: false,
			criteria_showing: {},
			comments_showing: {},
			new_comment: {},
			lreview: null,
			saved_review_json: '',
			templates: [],
			selected_template: undefined,
		}
	},

	computed: {
		...mapState(['site_config', 'user_info']),

		user_can_edit() {
			if (this.$parent.user_is_admin) return true
			const publish_status = this.$parent.local_item.publish_status
			const in_pipeline = ['submitted', 'under_review', 'ready_for_publication', 'published'].includes(publish_status)
			if (in_pipeline) return !!this.$parent.active_reviewer && publish_status === 'under_review'
			return this.$parent.publish_roles?.includes('author') ?? false
		},

		reviewer_or_publisher() {
			return this.$parent.user_is_admin || !!this.$parent.assigned_reviewer || (this.$parent.publish_roles ?? []).includes('publisher')
		},

		author() {
			return this.$parent.publish_roles.includes('author')
		},

		user_can_comment() {
			return this.$parent.publish_roles.length
		},

		reset_enabled() {
			if (this.author && this.lreview.asset_review_id) {
				return ["unsubmitted", "returned_to_author"].includes(this.$parent.current_publish_status.key)
			} else {
				return false
			}
		},

		sections() {
			return this.selected_template.definition.filter(x => {
				if (!x.asset_types || x.asset_types.includes(this.asset_type)) return x
			})
		},

		show_review_comments() {return true},

		item() { return this.$parent.item },

		asset_type() { return this.$parent.asset_type },

		asset_type_noun() { return this.$parent.asset_type_noun },

		total_score_pct() {
			if (empty(this.lreview)) return 0
			return (this.lreview.total_score / this.lreview.max_total_score) * 100
		},

		total_score_display() {
			return this.lreview.total_score || '-'
		},

		total_score_color() {
			if (empty(this.lreview) || this.total_score_display == '–') return 'k-review-grey'
			if (this.total_score_pct == 100) return 'green'
			if (this.total_score_pct > 75) return 'blue'
			if (this.total_score_pct > 50) return 'purple'
			if (this.total_score_pct > 25) return 'orange'
			return 'red'
		},
	},

	watch: {
		lreview: { deep:true, handler(v) {
			if (v && this.saved_review_json != JSON.stringify(this.lreview)) {
				this.save_review()
			}
		}},

		selected_template(v) {
			if (v) {
				this.set_local_review()
			}
		},
	},

	mounted() {
		vapp.asset_review_component = this

		this.templates = this.site_config.asset_review_templates || []

		if (this.asset_review_data.asset_review_id) {
			this.selected_template = object_copy(this.asset_review_data.template)
		}
	},

	methods: {
		set_local_review() {
			// make local copy of review
			this.lreview = new this.$Asset_Review(this.asset_review_data, this.selected_template, this.item)

			// set initial saved_review_json
			this.saved_review_json = JSON.stringify(this.lreview)

			let crs = {}
			let cms = {}
			let nc = {}
			for (let section of this.selected_template.definition) {
				// skip sections not relevant for this asset type
				if (section.asset_types && !section?.asset_types.includes(this.asset_type)) continue

				// set up criteria_showing, comments_showing, and new_comment so they are properly reactive
				crs[section.id] = false
				nc[section.id] = false
				if (section.criteria) {
					// if any individual criterion has been scored, show criteria for this section by default
					crs[section.id] = section.criteria.some(c => this.lreview.sections[section.id].criteria[c.id] !== null)
				}

				cms[section.id] = (this.lreview.sections[section.id].comments.length > 0)
			}
			this.criteria_showing = crs
			this.comments_showing = cms
			this.new_comment = nc
		},

		template_string(s) {
			return s.replace(/\$asset_type\b/g, this.asset_type_noun)
		},

		scale_score_class(section, score) {
			let prefix = this.user_can_edit ? '' : 'k-unclickable '

			if (score === null) return prefix
			if (score == 0) return prefix + 'k-asset-review-section-score-0'

			let scale_keys = Object.keys(section.scale)
			let index = scale_keys.findIndex(k => k == score)
			let last = scale_keys.length - 1
			let suffix = index !== last ? '-' + (last - index) : ''
			return prefix + 'k-asset-review-section-score-hi' + suffix
		},

		binary_score_class(score, section = null) {
			if (section && !section.required) {
				switch(score) {
					case null: return ''
					case 2: return 'k-asset-review-section-score-hi'
					case 1: return 'k-asset-review-section-score-0'
					default: return 'k-asset-review-section-score-hi-1'
				}
			}
			switch(score) {
				case null:
					return ''
				case 1:
					return 'k-asset-review-section-score-hi'
				default:
					return 'k-asset-review-section-score-0'
			}
		},

		scale_value_to_show(section) {
			let score = this.lreview.sections[section.id].score

			if (score === null) return '–'
			else return score
		},

		scale_icon(section, scale_value) {
			let score = this.lreview.sections[section.id].score
			return (score == scale_value) ? 'fas fa-circle-dot': 'far fa-circle'
		},

		binary_score_icon(section) {
			let score = this.lreview.sections[section.id].score

			if (!section.required) {
				switch(score) {
					case null: return 'fas fa-circle'
					case 2: return 'fas fa-circle-check'
					case 1: return 'fas fa-circle-xmark'
					default: return 'fas fa-circle-minus'
				}
			}

			switch(score) {
				case null:
					return 'fas fa-circle'
				case 1:
					return 'fas fa-circle-check'
				default:
					return 'fas fa-circle-xmark'
			}
		},

		// called when user clicks to set a value for a section
		scale_value_clicked(section, scale_value) {
			if (!this.user_can_edit) return

			// for standards_alignment section, score can't be > 0 if no standards are aligned
			if (section.id == 'standards_alignment') {
				if (scale_value > 0 && this.item.standards.length == 0) {
					this.$alert(`You cannot set the score for ${section.title} above 0, because the ${this.asset_type_noun} is not aligned to any standards.`)
					return
				}
			}

			this.lreview.sections[section.id].score = scale_value
			this.lreview.compute_total_score()
		},

		generate_new_comment(section_id) {
			this.new_comment[section_id] = new this.$Asset_Review_Comment({
				comment_id: 'new',
				author_email: this.user_info.email,
				author_name: `${this.user_info.first_name} ${this.user_info.last_name}`,
				author_id: this.user_info.user_id,
			})
		},

		async save_comment(section, comment) {
			let c = new this.$Asset_Review_Comment(comment)
			await c.set_created_at()

			if (c.comment_id == 'new') {
				await c.set_new_id()
				this.lreview.sections[section.id].comments.push(c)
			} else {
				let updated = this.lreview.sections[section.id].comments.find(({comment_id}) => comment_id == c.comment_id)
				Object.assign(updated, c)
			}
			
			this.new_comment[section.id] = false
		},

		delete_comment(section, comment) {
			this.lreview.sections[section.id].comments = this.lreview.sections[section.id].comments.filter(({comment_id}) => comment_id != comment.comment_id)
		},

		save_review() {
			// establish the debounce fn if necessary
			if (empty(this.fn_debounced)) {
				this.fn_debounced = U.debounce(function(x) {
					// for now we will send all data for the review in; we could instead only send updated data
					// note that for new reviews, asset_review_id will be 0 when we send, but will be set to a new integer when the service returns
					let updated_review_data = this.lreview.data_for_save()

					let payload = {
						user_id: this.user_info.user_id,
						// encode blocked keywords in the lp_data to avoid firewall issues; setting encode_blocked_keywords tells ajax to decode on the server
						updated_review_data: U.encode_blocked_keywords(JSON.stringify(updated_review_data)),
						encode_blocked_keywords: ['updated_review_data'],
					}
			
					U.ajax('save_asset_review', payload, result => {
						if (result.status != 'ok') {
							this.$alert('An error occurred when attempting to save the review. Has your session timed out?')
							return
						}

						// update the client review and reset saved_review_json
						this.lreview = new this.$Asset_Review(result.review_data, this.selected_template)
						this.saved_review_json = JSON.stringify(this.lreview)

						this.$emit('review_saved', this.lreview)
					})
				}, 100)
			}
			// call the debounce fn
			this.fn_debounced()
		},

		reset_review() {
			this.$confirm({
					title: 'Are you sure?',
					text: 'Are you sure you want to reset this review, including all associated comments? This cannot be undone.',
					acceptText: 'reset',
					acceptColor: 'red',
				}).then(() => {
					let payload = {
						user_id: this.user_info.user_id, 
						asset_review_id: this.lreview.asset_review_id
					}
					
					U.ajax('delete_asset_review', payload, result => {
						if (result.status != 'ok') {
							this.$alert('An error occurred when attempting to reset the review.')
							return
						}

						this.lreview = null
						this.selected_template = null
						this.$emit('review_saved', {})
					})
				}).catch(n => { console.error(n) })
		},

		criteria_changed(section, criteria, event) {
			let active_reviewer = this.$parent.active_reviewer
			if (this.site_config.asset_manager.review_auto_comment_on_criteria_change && active_reviewer) {
				let action = event ? 'Checked' : 'Unchecked'
				let text = '"' + criteria.text + '" - ' + action
				let comment = new this.$Asset_Review_Comment({
					comment_id: 'new',
					author_email: this.user_info.email,
					author_name: `${this.user_info.first_name} ${this.user_info.last_name}`,
					author_id: this.user_info.user_id,
					text
				})
				this.comments_showing[section.id] = true
				this.save_comment(section, comment)
			}
		},
	}
}
</script>

<style lang="scss">
.k-asset-review {
	margin:12px auto;
	border-radius:12px;
	padding:12px;
	font-size:14px;
	line-height:18px;
	max-width:740px;
	box-shadow: 0 1px 3px 1px rgba(64, 64, 64, 0.5);
}

.k-asset-review-section-extra {
	margin-top:8px;
	padding-top:8px;
	border-top:1px solid #aaa;
}

.k-asset-review-full-true {
	max-width:none;
}

.k-asset-review-header {
	font-size:16px;
}

.k-asset-review-section {
	border-top:1px solid #444;
	margin-top:12px;
	padding-top:12px;
}

.k-asset-review-section-title {
	font-size:16px;
}

.k-asset-review-section-score {
	width:32px;
	height:22px;
	line-height:22px;
	border-radius:4px;
	font-weight:bold;
	text-align:center;
	// this is the background-color and color when the score isn't set
	background-color: #ccc;
	color:#000;
}

.k-asset-review-section-score.k-asset-review-section-score-0 { background-color:$v-red-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-3 { background-color:$v-orange-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-2 { background-color:$v-purple-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi-1 { background-color:$v-blue-darken-4; color:#fff; }
.k-asset-review-section-score.k-asset-review-section-score-hi { background-color:$v-green-darken-4; color:#fff; }

.k-asset-review-section-scale-header {
	.v-icon.k-asset-review-section-score-0 { color:$v-red-darken-4; }
	.v-icon.k-asset-review-section-score-hi-3 { color:$v-orange-darken-4; }
	.v-icon.k-asset-review-section-score-hi-2 { color:$v-purple-darken-4; }
	.v-icon.k-asset-review-section-score-hi-1 { color:$v-blue-darken-4; }
	.v-icon.k-asset-review-section-score-hi { color:$v-green-darken-4; }

	.k-asset-review-section-scale-header-value.k-asset-review-section-score-0 { color:$v-red-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-3 { color:$v-orange-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-2 { color:$v-purple-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi-1 { color:$v-blue-darken-4; }
	.k-asset-review-section-scale-header-value.k-asset-review-section-score-hi { color:$v-green-darken-4; }
}

.k-asset-review-section-binary.k-asset-review-section-score-0 { color:$v-red-darken-4; }
.k-asset-review-section-binary.k-asset-review-section-score-hi { color:$v-green-darken-4; }
.k-asset-review-section-binary.k-asset-review-section-score-hi-1 { color:$v-blue-darken-4; }

.k-unclickable {
	cursor: default !important;
}

.k-review-read-only {
	opacity: 0.5;
	pointer-events: none;
}

.k-review-grey {
	background-color:#fff;
}

.v-progress-linear__background.k-review-grey {
	background-color:#999!important;
}
</style>
